/**
 * Logger Utility - Otimizado para Produção
 * 
 * Remove logs desnecessários em produção, mantendo apenas erros críticos.
 * Em desenvolvimento, permite debug detalhado via flag DEBUG_CHAT_PERF.
 */

/**
 * Verifica se debug está habilitado via:
 * - Variável de ambiente REACT_APP_DEBUG_CHAT_PERF
 * - Query param ?debugChatPerf=1
 * - localStorage DEBUG_CHAT_PERF=1
 * - window.DEBUG_CHAT_PERF
 */
const isDebugEnabled = () => {
    // Em SSR/build, logs detalhados ficam desabilitados por padrão.
    if (typeof window === 'undefined') return false;

    const queryFlag = new URLSearchParams(window.location.search).get('debugChatPerf') === '1';
    const storageFlag = window.localStorage?.getItem('DEBUG_CHAT_PERF') === '1';
    const windowFlag = window.DEBUG_CHAT_PERF;
    const envFlag = process.env.REACT_APP_DEBUG_CHAT_PERF === '1';

    // Logs detalhados apenas por opt-in explícito.
    return queryFlag || storageFlag || windowFlag || envFlag;
};

/**
 * Logger otimizado que só executa em modo debug
 */
export const logger = {
    /**
     * Log normal - apenas em modo debug
     */
    log: (...args) => {
        if (isDebugEnabled()) {
            console.log(...args);
        }
    },

    /**
     * Debug - apenas em modo debug (alias para log)
     */
    debug: (...args) => {
        if (isDebugEnabled()) {
            console.debug(...args);
        }
    },

    /**
     * Warning - apenas em modo debug
     */
    warn: (...args) => {
        if (isDebugEnabled()) {
            console.warn(...args);
        }
    },

    /**
     * Erro - sempre loga (crítico para produção)
     */
    error: (...args) => {
        console.error(...args);
    },

    /**
     * Performance measurement - apenas em modo debug
     * @param {string} label - Label para identificar a medição
     * @param {Function} fn - Função a ser medida
     * @returns {*} Resultado da função
     */
    perf: (label, fn) => {
        if (!isDebugEnabled()) {
            return fn();
        }

        const start = performance.now();
        const result = fn();
        const duration = (performance.now() - start).toFixed(2);
        console.log(`⚡ [PERF] ${label}: ${duration}ms`);
        return result;
    },

    /**
     * Performance measurement assíncrona - apenas em modo debug
     * @param {string} label - Label para identificar a medição
     * @param {Function} fn - Função assíncrona a ser medida
     * @returns {Promise<*>} Resultado da função
     */
    perfAsync: async (label, fn) => {
        if (!isDebugEnabled()) {
            return await fn();
        }

        const start = performance.now();
        const result = await fn();
        const duration = (performance.now() - start).toFixed(2);
        console.log(`⚡ [PERF] ${label}: ${duration}ms`);
        return result;
    },

    /**
     * Log condicional - apenas se condição for verdadeira E debug habilitado
     * @param {boolean} condition - Condição para logar
     * @param  {...any} args - Argumentos para console.log
     */
    logIf: (condition, ...args) => {
        if (condition && isDebugEnabled()) {
            console.log(...args);
        }
    },

    /**
     * Grupo de logs - apenas em modo debug
     * @param {string} label - Label do grupo
     * @param {Function} fn - Função que contém os logs
     */
    group: (label, fn) => {
        if (!isDebugEnabled()) return;

        console.group(label);
        fn();
        console.groupEnd();
    },

    /**
     * Tabela - apenas em modo debug
     * @param {*} data - Dados para exibir em tabela
     */
    table: (data) => {
        if (isDebugEnabled()) {
            console.table(data);
        }
    }
};

/**
 * Hook para verificar se debug está habilitado
 * Útil para componentes que precisam renderizar UI de debug
 */
export const useDebugMode = () => {
    return isDebugEnabled();
};

/**
 * Exporta flag de debug para uso direto
 */
export const DEBUG_ENABLED = isDebugEnabled();
